import React from "react";
import { Row } from "../../powerFlow/language";
import KeyIcon from "./KeyIcon";
import ColumnHeader from "./ColumnHeader";

interface Props {
  // The `demand` input relation: one row per (bus, timestep). Derived from the
  // grid and load curve (buildData), not read back out of the solved model —
  // demand is an input to the optimization.
  demand?: Row[];
}

/**
 * The `demand` input relation: the nodal withdrawal per (bus, timestep), keyed
 * by both.
 */
const DemandTable: React.FC<Props> = ({ demand }) => {
  const thStyle: React.CSSProperties = {
    background: "#f5f5f5",
    padding: "8px",
    textAlign: "left",
    borderBottom: "1px solid #ddd",
    fontWeight: "bold",
  };
  const tdStyle: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid #eee" };

  const rows = (demand ?? [])
    .map((r) => ({
      bus: String(r.bus ?? ""),
      timestep: Number(r.timestep ?? 0),
      value: Number(r.value ?? 0),
    }))
    .sort((a, b) => (a.bus === b.bus ? a.timestep - b.timestep : a.bus.localeCompare(b.bus)));

  if (rows.length === 0) {
    return (
      <div style={{ padding: "12px", color: "#666", fontStyle: "italic" }}>
        No demand yet. Add a load to the grid to populate the demand table.
      </div>
    );
  }

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", marginTop: "10px" }}>
      <thead>
        <tr>
          <ColumnHeader column="bus" style={thStyle}>
            bus<KeyIcon />
          </ColumnHeader>
          <ColumnHeader column="timestep" style={thStyle}>
            timestep<KeyIcon />
          </ColumnHeader>
          <ColumnHeader column="value" style={thStyle}>value (MW)</ColumnHeader>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.bus}_${r.timestep}`}>
            <td style={tdStyle}>{r.bus}</td>
            <td style={tdStyle}>{r.timestep}</td>
            <td style={tdStyle}>{r.value.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default DemandTable;
